import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EndUserContext } from "@/contexts/EndUserContext";
import { getLastSeen, hasUnseen, SCHOOL_NEWS_SEEN_EVENT } from "@/components/news/lastSeen";
import { fetchNotifications } from "@/services/schoolNews";

/** One cached answer per signed-in user, so a second login in the same tab asks again. */
export function schoolNewsEnabledKey(userId: string | null | undefined) {
  return ["school-news", "enabled", userId || "anonymous"] as const;
}

interface SchoolNewsStatus {
  enabled: boolean;
  /** When the newest item was added to this user's feed (ISO), or null. */
  latestAddedAt: string | null;
}

function useSchoolNewsStatus(): { userId: string | null; status: SchoolNewsStatus | undefined } {
  const userId = useContext(EndUserContext)?.authState?.userId ?? null;
  const { data } = useQuery({
    queryKey: schoolNewsEnabledKey(userId),
    queryFn: async (): Promise<SchoolNewsStatus> => {
      const page = await fetchNotifications({ limit: 1 });
      return { enabled: page.enabled === true, latestAddedAt: page.latestAddedAt ?? null };
    },
    staleTime: 5 * 60 * 1000,
    // The answer changes rarely; do not re-ask every time the tab regains focus.
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });
  return { userId, status: data };
}

/**
 * ZON-275 — is school news on for this user? True only when the API says so.
 * False while loading, on any error (402, 500, offline) and for accounts the
 * API excludes, so callers can simply hide their surface on false. A failed
 * check is retried once and never cached as "off".
 */
export function useSchoolNewsEnabled(): boolean {
  return useSchoolNewsStatus().status?.enabled === true;
}

/**
 * Should the Notifications nav item show its dot? True when school news is on
 * and something was added to the feed since this user last opened the
 * Notifications page in this browser. No count, by design. Shares the request
 * with useSchoolNewsEnabled.
 */
export function useSchoolNewsDot(): boolean {
  const { userId, status } = useSchoolNewsStatus();
  const [lastSeen, setLastSeen] = useState(() => getLastSeen(userId));

  useEffect(() => {
    const refresh = () => setLastSeen(getLastSeen(userId));
    refresh();
    window.addEventListener(SCHOOL_NEWS_SEEN_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SCHOOL_NEWS_SEEN_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [userId]);

  return status?.enabled === true && hasUnseen(status.latestAddedAt, lastSeen);
}
